import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "file-preview__header" }
const _hoisted_2 = { class: "file-preview__header-info" }
const _hoisted_3 = ["title"]
const _hoisted_4 = {
  key: 0,
  class: "file-preview__header-size"
}
const _hoisted_5 = {
  key: 0,
  class: "file-preview__fallback"
}
const _hoisted_6 = { class: "file-preview__fallback-text" }
const _hoisted_7 = {
  key: 0,
  class: "file-preview__image"
}
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = {
  key: 1,
  class: "file-preview__pdf-loading"
}
const _hoisted_10 = {
  key: 0,
  class: "file-preview__loading"
}
const _hoisted_11 = {
  key: 1,
  class: "file-preview__text"
}
const _hoisted_12 = ["src"]
const _hoisted_13 = ["src"]
const _hoisted_14 = ["href"]
const _hoisted_15 = ["href", "download"]

import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { Icon } from "@iconify/vue";
import VuePdfEmbed from "vue-pdf-embed";
import { Modal, Button, Spinner, type PreviewAttachment } from "@/components/ui";

interface Props {
  show: boolean;
  attachment: PreviewAttachment | null;
  size?: "md" | "lg" | "xl" | "xxl";
  zIndex?: number;
}

type Kind = "image" | "pdf" | "text" | "audio" | "video" | "unsupported";

const TEXT_SIZE_LIMIT = 1_048_576;

const PDF_MAX_RENDER_WIDTH = 600;


export default /*@__PURE__*/_defineComponent({
  __name: 'FilePreview',
  props: {
    show: { type: Boolean },
    attachment: {},
    size: {},
    zIndex: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const kind = computed<Kind>(() => {
  const a = props.attachment;
  if (!a || !a.url) return "unsupported";
  const m = a.mimeType || "";
  if (m === "application/pdf") return "pdf";
  if (m === "text/plain") return "text";
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("video/")) return "video";
  return "unsupported";
});

const resolvedSize = computed<"md" | "lg" | "xl" | "xxl" | "sm">(() => {
  if (props.size) return props.size;
  if (kind.value === "pdf" || kind.value === "image" || kind.value === "video") return "xl";
  if (kind.value === "audio") return "sm";
  return "md";
});

const categoryIcon = computed(() => {
  switch (kind.value) {
    case "pdf":
      return "mdi:file-pdf-box";
    case "image":
      return "mdi:file-image-outline";
    case "audio":
      return "mdi:file-music-outline";
    case "video":
      return "mdi:file-video-outline";
    case "text":
      return "mdi:file-document-outline";
    default:
      return "mdi:file";
  }
});

const iconBadgeStyle = computed(() => {
  const map: Record<Kind, { bg: string; fg: string }> = {
    pdf: { bg: "#fee2e2", fg: "#dc2626" },
    image: { bg: "#d1fae5", fg: "#059669" },
    audio: { bg: "#ede9fe", fg: "#7c3aed" },
    video: { bg: "#ffedd5", fg: "#ea580c" },
    text: { bg: "#dbeafe", fg: "#2563eb" },
    unsupported: { bg: "#f3f4f6", fg: "#6b7280" },
  };
  const c = map[kind.value];
  return { backgroundColor: c.bg, color: c.fg };
});

const textContent = ref("");
const textTooLarge = ref(false);
const loadingText = ref(false);
const mediaError = ref(false);
const pdfLoading = ref(false);
const pdfContainer = ref<HTMLElement | null>(null);
const pdfRenderWidth = ref<number | undefined>(undefined);
let abortController: AbortController | null = null;
let pdfResizeObserver: ResizeObserver | null = null;

function measurePdfWidth() {
  const el = pdfContainer.value;
  if (!el) return;
  const styles = getComputedStyle(el);
  const padX = parseFloat(styles.paddingLeft || "0") + parseFloat(styles.paddingRight || "0");
  const inner = Math.max(0, el.clientWidth - padX);
  if (inner > 0) pdfRenderWidth.value = Math.round(Math.min(inner, PDF_MAX_RENDER_WIDTH));
}

function attachPdfResize() {
  if (!pdfContainer.value || pdfResizeObserver) return;
  measurePdfWidth();
  pdfResizeObserver = new ResizeObserver(() => measurePdfWidth());
  pdfResizeObserver.observe(pdfContainer.value);
}

function detachPdfResize() {
  if (pdfResizeObserver) {
    pdfResizeObserver.disconnect();
    pdfResizeObserver = null;
  }
}

function onPdfError() {
  pdfLoading.value = false;
  mediaError.value = true;
}

const showFallback = computed(() => {
  if (!props.attachment || !props.attachment.url) return true;
  if (kind.value === "unsupported") return true;
  if (mediaError.value) return true;
  if (kind.value === "text" && textTooLarge.value) return true;
  return false;
});

const fallbackMessage = computed(() => {
  if (kind.value === "text" && textTooLarge.value) return "File is too large to preview";
  if (mediaError.value) return "Preview failed to load";
  if (!props.attachment || !props.attachment.url) return "No file to preview";
  return "Preview not available for this file type";
});

function resetState() {
  textContent.value = "";
  textTooLarge.value = false;
  loadingText.value = false;
  mediaError.value = false;
  pdfLoading.value = false;
  pdfRenderWidth.value = undefined;
  detachPdfResize();
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}

async function loadText(url: string) {
  abortController = new AbortController();
  const signal = abortController.signal;
  loadingText.value = true;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const len = Number(res.headers.get("content-length") ?? "0");
    if (len > TEXT_SIZE_LIMIT) {
      textTooLarge.value = true;
      return;
    }
    const reader = res.body!.getReader();
    let total = 0;
    const chunks: Uint8Array[] = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > TEXT_SIZE_LIMIT) {
        textTooLarge.value = true;
        await reader.cancel();
        return;
      }
      chunks.push(value);
    }
    if (signal.aborted) return;
    textContent.value = await new Blob(chunks).text();
  } catch (err) {
    if ((err as DOMException).name !== "AbortError") mediaError.value = true;
  } finally {
    loadingText.value = false;
  }
}

watch(
  () => [props.show, props.attachment?.id] as const,
  ([show]) => {
    resetState();
    if (!show || !props.attachment?.url) return;
    if (kind.value === "text") loadText(props.attachment.url);
    if (kind.value === "pdf") {
      pdfLoading.value = true;
      // Modal is teleported to body, mount happens after this tick.
      nextTick(() => attachPdfResize());
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (abortController) abortController.abort();
  detachPdfResize();
});

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    show: __props.show,
    size: resolvedSize.value,
    zIndex: __props.zIndex,
    onClose: _cache[4] || (_cache[4] = ($event: any) => (emit('close')))
  }, _createSlots({
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "file-preview__header-icon-wrap",
          style: _normalizeStyle(iconBadgeStyle.value)
        }, [
          _createVNode(_unref(Icon), {
            icon: categoryIcon.value,
            class: "file-preview__header-icon"
          }, null, 8, ["icon"])
        ], 4),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            class: "file-preview__header-name",
            title: __props.attachment?.fileName
          }, _toDisplayString(__props.attachment?.fileName || "Preview"), 9, _hoisted_3),
          (__props.attachment && __props.attachment.fileSize != null)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(formatFileSize(__props.attachment.fileSize)), 1))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["file-preview__body", { 'file-preview__body--audio': kind.value === 'audio' }])
      }, [
        (showFallback.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(Icon), {
                icon: "mdi:file",
                class: "file-preview__fallback-icon"
              }),
              _createElementVNode("p", _hoisted_6, _toDisplayString(fallbackMessage.value), 1)
            ]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (kind.value === 'image')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createElementVNode("img", {
                      src: __props.attachment!.url,
                      alt: __props.attachment!.fileName,
                      class: "file-preview__img",
                      onError: _cache[0] || (_cache[0] = ($event: any) => (mediaError.value = true))
                    }, null, 40, _hoisted_8)
                  ]))
                : (kind.value === 'pdf')
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: "file-preview__pdf",
                      ref_key: "pdfContainer",
                      ref: pdfContainer,
                      style: _normalizeStyle({ '--pdf-max-width': `${PDF_MAX_RENDER_WIDTH}px` })
                    }, [
                      (pdfRenderWidth.value != null)
                        ? _withDirectives((_openBlock(), _createBlock(_unref(VuePdfEmbed), {
                            key: 0,
                            source: __props.attachment!.url,
                            width: pdfRenderWidth.value,
                            class: "file-preview__pdf-embed",
                            onLoaded: _cache[1] || (_cache[1] = ($event: any) => (pdfLoading.value = false)),
                            onLoadingFailed: onPdfError
                          }, null, 8, ["source", "width"])), [
                            [_vShow, !pdfLoading.value]
                          ])
                        : _createCommentVNode("", true),
                      (pdfLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createVNode(_unref(Spinner), { size: "lg" }),
                            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "file-preview__pdf-loading-text" }, "Rendering PDF…", -1))
                          ]))
                        : _createCommentVNode("", true)
                    ], 4))
                  : (kind.value === 'text')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        (loadingText.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              _createVNode(_unref(Spinner), { size: "md" }),
                              _cache[6] || (_cache[6] = _createElementVNode("span", null, "Loading preview...", -1))
                            ]))
                          : (_openBlock(), _createElementBlock("pre", _hoisted_11, _toDisplayString(textContent.value), 1))
                      ], 64))
                    : (kind.value === 'audio')
                      ? (_openBlock(), _createElementBlock("audio", {
                          key: 3,
                          controls: "",
                          preload: "metadata",
                          src: __props.attachment!.url,
                          class: "file-preview__audio",
                          onError: _cache[2] || (_cache[2] = ($event: any) => (mediaError.value = true))
                        }, null, 40, _hoisted_12))
                      : (kind.value === 'video')
                        ? (_openBlock(), _createElementBlock("video", {
                            key: 4,
                            controls: "",
                            preload: "metadata",
                            src: __props.attachment!.url,
                            class: "file-preview__video",
                            onError: _cache[3] || (_cache[3] = ($event: any) => (mediaError.value = true))
                          }, null, 40, _hoisted_13))
                        : _createCommentVNode("", true)
            ], 64))
      ], 2)
    ]),
    _: 2
  }, [
    (__props.attachment && __props.attachment.url)
      ? {
          name: "footer",
          fn: _withCtx(() => [
            _createElementVNode("a", {
              href: __props.attachment.url,
              target: "_blank",
              rel: "noopener",
              class: "file-preview__footer-link"
            }, [
              _createVNode(_unref(Button), {
                variant: "secondary",
                size: "sm"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Icon), { icon: "mdi:open-in-new" }),
                  _cache[7] || (_cache[7] = _createTextVNode(" Open in new tab ", -1))
                ]),
                _: 1
              })
            ], 8, _hoisted_14),
            _createElementVNode("a", {
              href: __props.attachment.url,
              download: __props.attachment.fileName,
              target: "_blank",
              rel: "noopener",
              class: "file-preview__footer-link"
            }, [
              _createVNode(_unref(Button), {
                variant: "primary",
                size: "sm"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Icon), { icon: "mdi:tray-arrow-down" }),
                  _cache[8] || (_cache[8] = _createTextVNode(" Download ", -1))
                ]),
                _: 1
              })
            ], 8, _hoisted_15)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["show", "size", "zIndex"]))
}
}

})